/* eslint-disable react-hooks/exhaustive-deps */
import { useLazyQuery, useQuery } from '@apollo/client';
import { differenceInMinutes, parseISO, set } from 'date-fns';
import { loader } from 'graphql.macro';
import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { store } from '../app/store';
import {
    selectActiveChargieId,
    selectChargeDuration,
    selectChargeKwh,
    setChargeSessionFee,
    selectChargeStartTime,
    selectChargeStatus,
    selectInactiveChargieId,
    selectPenaltyStation,
    setActiveChargieId,
    setChargeAmp,
    setChargeCost,
    setChargeDuration,
    setChargeKw,
    setChargeKwh,
    setChargeStartTime,
    setChargeStatus,
    setErrorType,
    setFinishedTransaction,
    setInactiveChargieId,
    setPenaltyStation,
    setRequestedChargieId,
    selectDesiredConnector,
    setDesiredConnector,
} from '../appSlice';
import {
    ChargeStatus,
    RemoteErrorTypes,
    SecureStorageKeys,
} from '../utils/enums';
import { roundDown, roundDown4Digits } from '../utils/utility';
import { resolveRunningChargeCost } from '../utils/sessionFee';
import { logToNative, saveData } from '../utils/webview/messages';

interface WebSocketContextType {
    messages: string[];
}
const WebSocketContext = createContext<WebSocketContextType | undefined>(
    undefined
);

interface WebSocketProviderProps {
    children: ReactNode;
}
const getCurrentTransactionQuery = loader(
    '../gql/getCurrentTransaction.graphql'
);
const getRecentTransactionsForUserQuery = loader(
    '../containers/ChargingHistoryPage/getRecentTransactionsForUser.graphql'
);

const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
    const dispatch = useAppDispatch();
    const [messages, setMessages] = useState<string[]>([]);
    const socketRef = useRef<WebSocket | null>(null);
    const connectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const idleSocketTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const pollTimeoutCheckRef = useRef(true);
    // Have to keep this app selector for the useEffect
    const activeChargieId = useAppSelector(selectActiveChargieId);
    const inactiveChargieId = useAppSelector(selectInactiveChargieId);
    const chargeStatus = useAppSelector(selectChargeStatus);
    const chargeStartTime = useAppSelector(selectChargeStartTime);
    const isPenalty = useAppSelector(selectPenaltyStation);
    // const [isPenalty, setIsPenalty] = useState<boolean | undefined>(undefined);
    const navigate = useNavigate();
    const location = useLocation();
    const [timer, setTimer] = useState<number | null>(null);
    const [pollTimeout, setPollTimeout] = useState<number | null>(null);
    const chargeDuration = useAppSelector(selectChargeDuration);
    const connector = useAppSelector(selectDesiredConnector);
    const [getCurrentTransaction, { data: currentTransactionData }] =
        useLazyQuery(getCurrentTransactionQuery, {
            fetchPolicy: 'no-cache',
        });
    const [isLvl3, setIsLvl3] = useState(false);

    const [getRecentTransactionsForUser, { data: recentTransactionsData }] =
        useLazyQuery(getRecentTransactionsForUserQuery, {
            fetchPolicy: 'no-cache',
        });

    const [getStationDetails, { data: getStationPricingdata }] = useLazyQuery(
        getCurrentTransactionQuery,
        {
            fetchPolicy: 'no-cache',
        }
    );

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (
                document.visibilityState === 'visible' &&
                store.getState().app.activeChargieId
            ) {
                getCurrentTransaction();
            }
        };

        const handleSessionRefresh = () => {
            const chargieId = store.getState().app.activeChargieId;
            if (chargieId) {
                getCurrentTransaction();
                getStationDetails({
                    variables: { qrCode: chargieId },
                });
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener(
            'chargie:session-refresh',
            handleSessionRefresh
        );

        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );
            window.removeEventListener(
                'chargie:session-refresh',
                handleSessionRefresh
            );
        };
    }, []);

    useEffect(() => {
        if (
            !chargeStartTime ||
            chargeStatus === ChargeStatus.FINISHED ||
            chargeStatus === ChargeStatus.PREPARING ||
            timer
        ) {
            return;
        }

        const interval = window.setInterval(() => {
            const now = new Date();
            const current = Math.floor(
                (now.getTime() - chargeStartTime) / 1000
            );
            dispatch(setChargeDuration(current));
        }, 1000);
        setTimer(interval);
    }, [chargeStartTime]);
    useEffect(() => {
        if (chargeDuration === null && timer) {
            clearInterval(timer);
        }
    }, [chargeDuration]);

    const updateStatus = (status: string) => {
        const newStatus = status.toUpperCase();
        if (
            chargeStartTime === null &&
            (newStatus === ChargeStatus.SUSPENDEDEV ||
                newStatus === ChargeStatus.CHARGING ||
                newStatus === ChargeStatus.SUSPENDEDEVSE ||
                newStatus === ChargeStatus.IDLE)
        ) {
            const now = new Date();
            dispatch(setChargeStartTime(now.getTime()));
        }

        if (newStatus === ChargeStatus.PENDING) {
            dispatch(setChargeStatus(ChargeStatus.PREPARING));
            dispatch(setChargeDuration(null));
            dispatch(setChargeStartTime(null));
        } else if (
            newStatus === ChargeStatus.SUSPENDEDEV ||
            newStatus === ChargeStatus.SUSPENDEDEVSE
        ) {
            dispatch(setChargeStatus(ChargeStatus.IDLE));
        } else if (newStatus === ChargeStatus.COMPLETE && isPenalty) {
            dispatch(setChargeStatus(ChargeStatus.FINISHING));
        } else {
            const currentStatus = store.getState().app
                .chargeStatus as ChargeStatus;
            if (
                (currentStatus === ChargeStatus.PENDING ||
                    currentStatus === ChargeStatus.PREPARING) &&
                (newStatus === ChargeStatus.AVAILABLE ||
                    newStatus === ChargeStatus.ABORTED)
            ) {
                dispatch(setErrorType(RemoteErrorTypes.EV_NOT_DETECTED));
                if (location.pathname === '/session') {
                    navigate('/', { replace: true });
                }
            }
            dispatch(setChargeStatus(newStatus));
        }
    };

    useEffect(() => {
        if (getStationPricingdata) {
            try {
                if (
                    !getStationPricingdata.loggedInUser?.currentTransaction
                        ?.transactionCache
                ) {
                    return;
                }
                const item = JSON.parse(
                    getStationPricingdata.loggedInUser?.currentTransaction
                        ?.transactionCache || '{}'
                );

                if (item?.stationLevel === 3) setIsLvl3(true);
                else setIsLvl3(false);
                dispatch(setPenaltyStation(item.isPenalty));
                const startTime = new Date(item.chargeStartTime);
                const currentStatus = item.chargeStatus.toUpperCase();
                if (
                    currentStatus === ChargeStatus.ABORTED ||
                    currentStatus === ChargeStatus.FINALIZED ||
                    (currentStatus === ChargeStatus.COMPLETE &&
                        !item.isPenalty &&
                        (item.penalty !== 0 || item.penalty.delayMinutes !== 0))
                ) {
                    resetChargeStatus();
                    return;
                } else if (
                    currentStatus === ChargeStatus.COMPLETE &&
                    item.isPenalty
                ) {
                    dispatch(setChargeStatus(ChargeStatus.FINISHING));
                }
                if (
                    !chargeStartTime &&
                    (currentStatus === ChargeStatus.CHARGING ||
                        currentStatus === ChargeStatus.SUSPENDEDEV ||
                        currentStatus === ChargeStatus.SUSPENDEDEVSE ||
                        currentStatus === ChargeStatus.IDLE)
                ) {
                    dispatch(setChargeStartTime(startTime.getTime()));
                }
                dispatch(setChargeSessionFee(item?.sessionFee));
                dispatch(
                    setChargeAmp(
                        item.ampHistoryWithStatus.length > 0 &&
                            item.ampHistoryWithStatus[0].length > 0
                            ? Math.floor(item.ampHistoryWithStatus[0][0])
                            : 0
                    )
                );
                const totalWh =
                    item.lastKnownWh - (item.startingWh || item.firstKnownWh) ||
                    0;
                dispatch(setChargeStatus(item.chargeStatus.toUpperCase()));
                dispatch(setChargeKwh(totalWh / 1000));
                dispatch(setChargeKw((item.liveWatts || 0) / 1000));
                if (
                    currentStatus.toUpperCase() === ChargeStatus.FINISHING ||
                    currentStatus.toUpperCase() === ChargeStatus.COMPLETE
                ) {
                    dispatch(setChargeCost(item.totalCost || 0));
                }
            } catch (e) {
                console.log(e);
            }
        } else if (!activeChargieId || activeChargieId == '') {
            resetChargeStatus();
        }
    }, [getStationPricingdata]);

    useEffect(() => {
        if (
            (chargeStatus === ChargeStatus.SUSPENDEDEV ||
                chargeStatus === ChargeStatus.SUSPENDEDEVSE ||
                chargeStatus === ChargeStatus.CHARGING ||
                chargeStatus === ChargeStatus.IDLE) &&
            !chargeStartTime
        ) {
            if (pollTimeout) {
                clearInterval(pollTimeout);
                setPollTimeout(null);
            }
            const now = new Date();
            dispatch(setChargeStartTime(now.getTime()));
        } else if (
            chargeStatus === ChargeStatus.QUEUED ||
            chargeStatus === ChargeStatus.PENDING ||
            chargeStatus === ChargeStatus.PREPARING ||
            chargeStatus === ChargeStatus.LOADING ||
            chargeStatus === ChargeStatus.COMPLETE
        ) {
            if (
                activeChargieId &&
                !pollTimeout &&
                pollTimeoutCheckRef.current
            ) {
                pollTimeoutCheckRef.current = false;
                setTimeout(() => {
                    const pollTimeoutInterval = window.setInterval(
                        async () => await getCurrentTransaction(),
                        30000
                    );
                    setPollTimeout(pollTimeoutInterval);
                }, 5000);
            } else {
                if (!(chargeStatus === ChargeStatus.COMPLETE && isPenalty)) {
                    dispatch(setChargeDuration(null));
                    dispatch(setChargeStartTime(null));
                }
            }
        } else {
            if (pollTimeout) {
                clearInterval(pollTimeout);
                setPollTimeout(null);
            }
        }
        if (
            chargeStatus === ChargeStatus.SUSPENDEDEV ||
            chargeStatus === ChargeStatus.SUSPENDEDEVSE
        )
            dispatch(setChargeStatus(ChargeStatus.IDLE));
        else if (chargeStatus === ChargeStatus.PENDING)
            dispatch(setChargeStatus(ChargeStatus.PREPARING));
        if (
            chargeStatus === ChargeStatus.ABORTED ||
            chargeStatus === ChargeStatus.FINALIZED
        ) {
            resetChargeStatus();
        } else if (
            (chargeStatus === ChargeStatus.COMPLETE ||
                chargeStatus === ChargeStatus.FINISHING) &&
            isPenalty === false &&
            !inactiveChargieId
        ) {
            if (
                store.getState().app.activeChargieId &&
                store.getState().app.activeChargieId !== ''
            ) {
                dispatch(
                    setInactiveChargieId(store.getState().app.activeChargieId)
                );
            }
            setTimeout(async () => {
                await getRecentTransactionsForUser();
                resetChargeStatus();
                return;
            }, 9000);
        } else if (chargeStatus === ChargeStatus.COMPLETE && isPenalty) {
            dispatch(setChargeStatus(ChargeStatus.FINISHING));
        }
    }, [chargeStatus, isPenalty]);

    useEffect(() => {
        const performActions = async () => {
            try {
                if (!isLvl3) {
                    if (
                        chargeStatus === ChargeStatus.CHARGING ||
                        chargeStatus === ChargeStatus.IDLE ||
                        chargeStatus === ChargeStatus.FINISHED ||
                        chargeStatus === ChargeStatus.COMPLETE
                    ) {
                        if (pollTimeout) {
                            clearInterval(pollTimeout);
                            setPollTimeout(null);
                        }
                    }
                }
                if (
                    currentTransactionData.loggedInUser?.currentTransaction
                        ?.transactionCache &&
                    currentTransactionData.loggedInUser?.currentTransaction
                        ?.transactionCacheDetails
                ) {
                    const parsedCache = JSON.parse(
                        currentTransactionData.loggedInUser.currentTransaction
                            .transactionCache
                    );
                    dispatch(setDesiredConnector(parsedCache.connector));
                    dispatch(setPenaltyStation(parsedCache.isPenalty));
                    const chargeStart = new Date(parsedCache.chargeStartTime);
                    if (!chargeStartTime) {
                        store.dispatch(
                            setChargeStartTime(chargeStart.getTime())
                        );
                    }
                    if (isLvl3) {
                        const start = parsedCache.startingWh || 0;
                        dispatch(
                            setChargeKwh(
                                roundDown4Digits(
                                    (parsedCache.lastKnownWh - start) / 1000 ||
                                        0
                                )
                            )
                        );
                    }
                    if (
                        socketRef &&
                        socketRef.current?.readyState === WebSocket.CLOSED
                    ) {
                        await connect();
                    }

                    if (
                        (parsedCache.chargeStatus.toUpperCase() ===
                            ChargeStatus.COMPLETE ||
                            parsedCache.chargeStatus.toUpperCase() ===
                                ChargeStatus.FINISHING) &&
                        parsedCache.isPenalty
                    ) {
                        if (chargeStatus !== ChargeStatus.FINISHING) {
                            dispatch(setChargeStatus(ChargeStatus.FINISHING));
                        }
                        const now = new Date();
                        const diffInMs = now.getTime() - chargeStart.getTime(); // Difference in milliseconds
                        const diffInMinutes = Math.floor(
                            diffInMs / (1000 * 60)
                        );
                        dispatch(setChargeAmp(0));
                        dispatch(setChargeKw(0));
                        if (
                            diffInMinutes >=
                                (parsedCache.pricingPolicy
                                    ?.penaltyDelayMinutes || 0) &&
                            diffInMinutes > 15
                        ) {
                            const penaltyHours = Math.ceil(
                                (diffInMinutes - 15) / 60
                            );
                            if (penaltyHours > 0) {
                                const baseCost = resolveRunningChargeCost(
                                    parseFloat(
                                        parsedCache.totalCost.toFixed(2)
                                    ),
                                    parsedCache.sessionFee
                                );
                                dispatch(
                                    setChargeCost(
                                        baseCost +
                                            penaltyHours *
                                                (parsedCache.pricingPolicy
                                                    .penalty || 0)
                                    )
                                );
                            } else {
                                dispatch(
                                    setChargeCost(
                                        parseFloat(
                                            parsedCache.totalCost.toFixed(2)
                                        )
                                    )
                                );
                            }
                        } else {
                            dispatch(
                                setChargeCost(
                                    parseFloat(parsedCache.totalCost.toFixed(2))
                                )
                            );
                        }
                    } else {
                        dispatch(
                            setChargeStatus(
                                parsedCache.chargeStatus.toUpperCase()
                            )
                        );
                        if (
                            parsedCache.chargeStatus.toUpperCase() ===
                                ChargeStatus.COMPLETE ||
                            parsedCache.chargeStatus.toUpperCase() ===
                                ChargeStatus.FINISHING
                        ) {
                            dispatch(
                                setChargeCost(parsedCache.totalCost.toFixed(2))
                            );
                        }
                        dispatch(
                            setChargeAmp(
                                roundDown(
                                    parsedCache.ampHistoryWithStatus &&
                                        parsedCache.ampHistoryWithStatus
                                            .length > 0
                                        ? parsedCache.ampHistoryWithStatus[0][0]
                                        : 0
                                )
                            )
                        );
                        dispatch(
                            setChargeKw(
                                (parsedCache.wattsHistory &&
                                parsedCache.wattsHistory.length > 0
                                    ? parsedCache.wattsHistory[0]
                                    : 0) / 1000
                            )
                        );
                    }
                } else {
                    if (isPenalty && activeChargieId && !inactiveChargieId) {
                        setTimeout(async () => {
                            await getRecentTransactionsForUser();
                            dispatch(setInactiveChargieId(activeChargieId));
                            resetChargeStatus();
                        }, 3000);
                    } else {
                        resetChargeStatus();
                    }
                }
            } catch {}
        };
        performActions();
    }, [currentTransactionData]);

    const resetChargeStatus = () => {
        saveData(SecureStorageKeys.ACTIVE_CHARGIE_ID, '');
        dispatch(setChargeDuration(null));
        dispatch(setChargeStartTime(null));
        dispatch(setChargeAmp(0));
        dispatch(setChargeKwh(0));
        dispatch(setChargeKw(0));
        dispatch(setChargeCost(0));
        dispatch(setChargeStatus(ChargeStatus.LOADING));
        dispatch(setActiveChargieId(null));
        dispatch(setRequestedChargieId(undefined));
        dispatch(setPenaltyStation(undefined));
        dispatch(setDesiredConnector(null));
        if (timer) {
            clearInterval(timer);
            setTimer(null);
        }
        if (pollTimeout) {
            clearInterval(pollTimeout);
            setPollTimeout(null);
        }
        pollTimeoutCheckRef.current = true;
        if (location.pathname === '/session') navigate('/', { replace: true });
    };

    const socketOnOpen = () => {
        const date = new Date();
        console.log('WebSocket connected!', date.toISOString());
        if (connectTimeoutRef.current) {
            clearTimeout(connectTimeoutRef.current);
            connectTimeoutRef.current = null;
        }
        if (idleSocketTimeoutRef.current) {
            clearTimeout(idleSocketTimeoutRef.current);
        }
        idleSocketTimeoutRef.current = setTimeout(() => {
            socketRef.current?.close(3001, 'on open idle close');
        }, 60000);
        // Refetch the current transaction details
        setTimeout(() => {
            getCurrentTransaction();
        }, 10000);
    };

    const socketOnMessage = (event: MessageEvent<any>) => {
        const message = event.data as string;
        logToNative('WebSocket message! ' + message);
        console.log('WebSocket message! ' + message);
        const item = JSON.parse(message);
        // Filter messages by connector - only process messages for the current user's connector
        // If the message has a connector field, it must match the current user's connector
        if (
            item.connector !== undefined &&
            item.connector !== store.getState().app.desiredConnector
        ) {
            return;
        }
        if (item.status) {
            updateStatus(item.status);
        } else if (
            chargeStatus === ChargeStatus.COMPLETE ||
            chargeStatus === ChargeStatus.FINISHING ||
            (chargeStatus === ChargeStatus.FINISHED &&
                item.isComplete &&
                item.event === 'ChangeCompletion' &&
                isPenalty)
        ) {
            setTimeout(() => {
                getRecentTransactionsForUser();
                dispatch(
                    setInactiveChargieId(store.getState().app.activeChargieId)
                );
                resetChargeStatus();
                return;
            }, 3500);
        } else if (!isLvl3) {
            //uses socket if not level 2 since connector specific socket doesn't exist yet
            const { chargeSessionFee } = store.getState().app;
            dispatch(setChargeAmp(Math.floor(item.amps)));
            dispatch(setChargeKwh(item.kwh || 0));
            dispatch(setChargeKw(item.liveKw || 0));
            dispatch(setChargeCost(item.cost || 0));
        }
        if (idleSocketTimeoutRef.current) {
            clearTimeout(idleSocketTimeoutRef.current);
        }
        idleSocketTimeoutRef.current = setTimeout(() => {
            socketRef.current?.close(3001, 'on message idle close');
        }, 60000);
    };

    const socketOnClose = (e: CloseEvent) => {
        console.log('WebSocket closed', e);
        logToNative('Websocket closed');
        if (
            socketRef &&
            socketRef.current &&
            socketRef.current.readyState === WebSocket.OPEN
        )
            return;
        if (e.code !== 3000) {
            // Attempt reconnecting every 5 seconds if not already retrying
            if (!connectTimeoutRef.current) {
                connectTimeoutRef.current = setTimeout(() => connect(), 2000);
            }
        }
        if (idleSocketTimeoutRef.current) {
            clearTimeout(idleSocketTimeoutRef.current);
            idleSocketTimeoutRef.current = null;
        }
        return;
    };

    const socketOnError = (e: Event) => {
        console.error('WebSocket error', e);
        socketRef.current?.close(3001, 'Websocket error');
    };

    const setupTimeout = () => {
        connectTimeoutRef.current = setTimeout(() => {
            if (socketRef.current?.readyState !== WebSocket.OPEN) {
                console.error('WebSocket connection timed out');
                socketRef.current?.close(
                    3001,
                    'Websocket connection timed out'
                ); // This will trigger the onclose handler
            }
        }, 1000); // 1 seconds timeout for connection
    };

    const connect = async () => {
        if (
            socketRef &&
            socketRef.current &&
            socketRef.current.readyState === WebSocket.OPEN
        )
            return;
        if (store.getState().app.activeChargieId) {
            const wss = `${process.env.REACT_APP_WEBSOCKET_ENDPOINT}/v2/station/${store.getState().app.activeChargieId}/progress/socket`;
            const newSocket = new WebSocket(wss);
            newSocket.onopen = socketOnOpen;
            newSocket.onmessage = socketOnMessage;
            newSocket.onclose = socketOnClose;
            newSocket.onerror = socketOnError;

            // Close the socket after a 1 second timeout
            // Closing the socket will trigger a socket reinitialization
            setupTimeout();
            socketRef.current = newSocket;
        }
    };

    useEffect(() => {
        if (activeChargieId && activeChargieId !== '') {
            // Get current transaction immediately to set connector state before WebSocket connection
            getCurrentTransaction();
            connect();
            getStationDetails({
                variables: { qrCode: store.getState().app.activeChargieId },
            });
        } else {
            socketRef.current?.close(3000, 'no more active chargie id close');
        }
    }, [activeChargieId]);

    useEffect(() => {
        if (
            inactiveChargieId &&
            recentTransactionsData?.recentTransactionsForUser
        ) {
            const lastTransactionForStation =
                recentTransactionsData.recentTransactionsForUser.find(
                    (t: any) => t.station?.qrCode === inactiveChargieId
                );
            if (
                lastTransactionForStation &&
                lastTransactionForStation.startedAt &&
                lastTransactionForStation.stoppedAt
            ) {
                const dt1 = parseISO(lastTransactionForStation.startedAt);
                const dt2 = parseISO(lastTransactionForStation.stoppedAt);
                const duration = differenceInMinutes(dt2, dt1);
                dispatch(
                    setFinishedTransaction({
                        kwh: lastTransactionForStation.wh / 1000,
                        duration,
                        cost: lastTransactionForStation.totalAmount,
                    })
                );
            }
        }
    }, [inactiveChargieId, recentTransactionsData]);

    // Close the socket on unmount
    useEffect(() => {
        return () => socketRef.current?.close(3000, 'unmount close');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <WebSocketContext.Provider value={{ messages }}>
            {children}
        </WebSocketContext.Provider>
    );
};

const useWebSocket = (): WebSocketContextType => {
    const context = useContext(WebSocketContext);
    if (context === undefined) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }
    return context;
};

export { WebSocketProvider, useWebSocket };
